import { Check, Close, InfoOutlined } from '@mui/icons-material';
import { Autocomplete, Chip, Box, Typography, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { greyChipStyles } from 'domains/core/components/Chip/Chip';
import InputLabel from 'domains/core/components/InputLabel';
import TextField from 'domains/core/components/TextField';
import { DisplayConditionOption } from './BeeEditorTestSendDialog';

type DisplayConditionOptionMultiSelectProps = {
    disabled?: boolean;
    onChange?: (value: DisplayConditionOption[]) => void;
    options?: DisplayConditionOption[];
    selectedValues?: DisplayConditionOption[];
};

const DisplayConditionOptionMultiSelect = ({
    options = [],
    disabled = false,
    onChange,
    selectedValues = [],
}: DisplayConditionOptionMultiSelectProps) => {
    const [filteredSelectedValues, setFilteredSelectedValues] = useState<DisplayConditionOption[]>(selectedValues);

    // Filter out selected values that are no longer in the options list
    useEffect(() => {
        const validSelectedValues = selectedValues.filter((selectedValue) =>
            options.some((option) => option.id === selectedValue.id)
        );

        if (validSelectedValues.length !== selectedValues.length) {
            setFilteredSelectedValues(validSelectedValues);
            onChange?.(validSelectedValues);
        } else {
            setFilteredSelectedValues(selectedValues);
        }
    }, [options, selectedValues, onChange]);

    return (
        <Autocomplete
            disableCloseOnSelect
            disabled={disabled}
            getOptionLabel={(option: DisplayConditionOption) => option?.label}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            limitTags={2}
            multiple
            onChange={(event: any, newValue: DisplayConditionOption[]) => onChange?.(newValue)}
            options={options}
            renderInput={(params: any) => (
                <TextField
                    {...params}
                    variant="outlined"
                    label={
                        <InputLabel
                            label="Display Conditions"
                            labelIcon={<InfoOutlined color={disabled ? 'disabled' : 'inherit'} fontSize="inherit" />}
                            labelIconTooltipText="Select the display conditions for the test email"
                        />
                    }
                />
            )}
            renderOption={(props, option, { selected }) => (
                <Box component="li" {...props} sx={{}}>
                    <Stack direction={'column'}>
                        <Stack direction="row">
                            <Typography variant="body1" sx={{ color: selected ? '#00845B' : 'inherit' }}>
                                {option.label}
                            </Typography>
                            {selected && <Check sx={{ color: '#00845B' }} />}
                        </Stack>
                        <Stack>
                            <Typography variant="body2" color="text.secondary" component="div">
                                {option.description}
                            </Typography>
                        </Stack>
                    </Stack>
                </Box>
            )}
            renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                    <Chip
                        deleteIcon={<Close />}
                        label={option.label}
                        {...getTagProps({ index })}
                        sx={{ ...greyChipStyles }}
                    />
                ))
            }
            value={filteredSelectedValues}
        />
    );
};

export default DisplayConditionOptionMultiSelect;
